'use client'

/**
 * What an entry kind looks like — one record, keyed by `EntryType`.
 *
 * Nine sites answered this question before step 2, with no interface over them and no agreement:
 * `ui/status-colors.ts:90-131`, `unified/feed-constants.ts:25-71`, `ui/type-indicator.tsx:31-51`,
 * `unified/form-sections/form-title-section.tsx:15-43`, `app/app/people/components/EntryRow.tsx:21-64`,
 * `library/media-preview/constants.ts:7-21`, `capture/capture-chips-row.tsx:24-32`,
 * `search/search-result-row.tsx:40-55` and `filters/entry-type-filter.tsx:16-24`. They disagreed on
 * keys, on shape, and on labels. ADR 0011:130-135 is the decision: one
 * `Record<EntryType, EntryPresentation>` carrying `icon`, `label`, and all six distinct class
 * fields, so each call site takes the field it took before.
 *
 * **This record is a sibling of the registry, not a field on `EntrySlice`** (ADR 0011:137-143).
 * Presentation is per *kind*; the registry binds eight kinds onto four slices, media being one
 * slice over five kinds. One `icon` on a slice five keys share cannot hold three icons.
 *
 * **The colour is one `--entry-*` token per kind**, declared in `globals.css`. Every class field
 * below is derived from it, which is what `classesFor` records: the nine sites had written the
 * same derivation out by hand, inconsistently. `person` takes the *book* token — that is the
 * choice `status-colors.ts:101-105` shipped, preserved here rather than re-decided.
 *
 * **The directive is load-bearing.** The record holds Phosphor icon *components*, and
 * `@phosphor-icons/react` calls `createContext` at module scope. `app/app/journal/page.tsx` is a
 * server component that imports `@/entries`, which pulls this module into the RSC graph — without
 * `'use client'` the build fails collecting that route with `createContext is not a function`, and
 * nothing before `pnpm build` says so (#375).
 */

import {
  BookOpenIcon,
  CheckSquareIcon,
  FileTextIcon,
  FilmSlateIcon,
  NotebookIcon,
  UserIcon,
  WaveformIcon,
} from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'

import type { EntryType } from '@/document'

/**
 * How one entry kind presents itself.
 *
 * The six class fields are not six colours. They are six *shapes* the merged sites asked the one
 * token for: a background wash (`bg`, at 15% — `status-colors.ts`), the same wash at 10% for a
 * badge (`badgeBg` — `feed-constants.ts`), the foreground (`text`, which `feed-constants.ts` also
 * called `icon` and `badge`), the two composed (`combined`), and the feed row's left border on
 * hover (`hoverBorder`).
 */
export interface EntryPresentation {
  /** Phosphor icon component. Callers pass `className` and `weight` as before. */
  icon: Icon
  /** Singular, title case: "Journal", "Release". */
  label: string
  /** `bg-entry-x/15` — the icon chip and badge wash. */
  bg: string
  /** `text-entry-x` — the foreground. */
  text: string
  /** `bg` and `text` together, in that order. */
  combined: string
  /** The feed badge's foreground. Identical to `text`; both survive because both are read. */
  badge: string
  /** `bg-entry-x/10` — the feed badge wash, lighter than `bg`. */
  badgeBg: string
  /** `hover:border-l-entry-x` — the feed row's left border on hover. */
  hoverBorder: string
}

function classesFor(token: string) {
  return {
    bg: `bg-${token}/15`,
    text: `text-${token}`,
    combined: `bg-${token}/15 text-${token}`,
    badge: `text-${token}`,
    badgeBg: `bg-${token}/10`,
    hoverBorder: `hover:border-l-${token}`,
  }
}

/**
 * The total record. A kind in `EntryType` with no presentation is a compile error naming the miss —
 * the same property the registry gets from its mapped type.
 */
export const ENTRY_PRESENTATION: Record<EntryType, EntryPresentation> = {
  journal: {
    icon: NotebookIcon,
    label: 'Journal',
    ...classesFor('entry-journal'),
  },
  task: { icon: CheckSquareIcon, label: 'Task', ...classesFor('entry-task') },
  person: { icon: UserIcon, label: 'Person', ...classesFor('entry-book') },
  movie: { icon: FilmSlateIcon, label: 'Movie', ...classesFor('entry-movie') },
  book: { icon: BookOpenIcon, label: 'Book', ...classesFor('entry-book') },
  artist: { icon: WaveformIcon, label: 'Artist', ...classesFor('entry-music') },
  label: { icon: WaveformIcon, label: 'Label', ...classesFor('entry-music') },
  release: {
    icon: WaveformIcon,
    label: 'Release',
    ...classesFor('entry-music'),
  },
}

/**
 * What an entry of a kind this record does not hold looks like.
 *
 * `search-result-row.tsx:57` had this as its own `DEFAULT_CONFIG`; it is published because the need
 * is the seam's, not that file's. `Entry.type` is `EntryType | string` (#151 narrowed the union and
 * nothing narrowed the field), and the tree holds a live writer of an undeclared kind — `'album'`,
 * at `useMediaMutations.ts:306` (#395).
 */
export const FALLBACK_PRESENTATION: EntryPresentation = {
  icon: FileTextIcon,
  label: 'Entry',
  bg: 'bg-muted',
  text: 'text-muted-foreground',
  combined: 'bg-muted text-muted-foreground',
  badge: 'text-muted-foreground',
  badgeBg: 'bg-muted',
  hoverBorder: 'hover:border-l-muted-foreground',
}

/**
 * The door. One runtime narrow at the seam, instead of a cast plus a `??` at every call site.
 *
 * Takes the widened `Entry.type` as it is written, so a caller never spells `as EntryType`.
 */
export function presentationFor(
  type: EntryType | string | null | undefined,
): EntryPresentation {
  if (type == null) return FALLBACK_PRESENTATION
  return ENTRY_PRESENTATION[type as EntryType] ?? FALLBACK_PRESENTATION
}
